import ResourceSvc from '@/services/resource.svc.js';

import ClubDetailsSection from './sections/club-details-section/club-details-section.vue';
import OfficersSection from './sections/officers-section/officers-section.vue';
import JuniorMembersSection from './sections/junior-members-section/junior-members-section.vue';
import BankDetailsSection from './sections/bank-details-section/bank-details-section.vue';
import MembershipTypesSection from './sections/membership-types-section/membership-types-section.vue';
import DocumentsSettingsSection from './sections/documents-settings-section/documents-settings-section.vue';
import ReviewSubmitSection from './sections/review-submit-section/review-submit-section.vue';
import ConfirmDialog from '@/components/shared/confirm-dialog/confirm-dialog.vue';

import {
    CLUB_AFFILIATION_YEAR,
    FORM_SECTIONS,
    IRISH_COUNTIES,
    createEmptyOfficer,
    createInitialClubState,
    createInitialValidationState,
    normalizeOfficer,
    normalizeAdditionalOfficer,
    generateYearOptions,
    formatCountyOptions,
    parseIntOrNull,
    toStringOrEmpty,
    isClubRegistrationFormValid,
    hasClubRegistrationValidationErrors,
    buildMembershipTypeRemovalWarning
} from './constants/club-registration-constants.js';

const resourceService = new ResourceSvc();

export default {
    name: 'registerClub',

    provide() {
        return {
            highlight_invalid_fields: true
        };
    },

    props: ['clubId'],

    components: {
        'club-details-section': ClubDetailsSection,
        'junior-members-section': JuniorMembersSection,
        'officers-section': OfficersSection,
        'bank-details-section': BankDetailsSection,
        'membership-types-section': MembershipTypesSection,
        'documents-settings-section': DocumentsSettingsSection,
        'review-submit-section': ReviewSubmitSection,
        'confirm-dialog': ConfirmDialog
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            isLoadingCopy: false,
            isRenewalOptInLocked: false,
            errorMessage: null,
            activeSection: 'details',

            sections: FORM_SECTIONS,

            initData: {
                yearOptions: [],
                countyOptions: [],
                availableMembershipTypes: []
            },

            assignedMembershipTypes: [],
            acknowledgedMembershipTypeRemovals: false,
            isRemovalWarningVisible: false,
            removalWarning: null,
            selectedMembershipTypeId: null,
            selectedMembershipTypePrice: null,
            newMembershipTypeName: '',
            nextTemporaryMembershipTypeId: -1000,
            membershipFieldValidation: {
                selectedMembershipType: {
                    valid: true,
                    errorMessages: []
                },
                membershipTypePrice: {
                    valid: true,
                    errorMessages: []
                },
                newMembershipTypeName: {
                    valid: true,
                    errorMessages: []
                }
            },

            copyOptions: [],
            selectedCopyId: null,

            club: createInitialClubState(),

            validation: createInitialValidationState()
        };
    },
    computed: {
        currentYear() {
            return new Date().getFullYear();
        },

        affiliationYear() {
            return CLUB_AFFILIATION_YEAR;
        },

        currentSectionIndex() {
            return this.sections.findIndex(s => s.id === this.activeSection);
        },

        isFirstSection() {
            return this.currentSectionIndex === 0;
        },

        isLastSection() {
            return this.currentSectionIndex === this.sections.length - 1;
        },

        nextSectionId() {
            const nextIndex = this.currentSectionIndex + 1;
            return nextIndex < this.sections.length ? this.sections[nextIndex].id : null;
        },

        previousSectionId() {
            const prevIndex = this.currentSectionIndex - 1;
            return prevIndex >= 0 ? this.sections[prevIndex].id : null;
        },

        isFormValid() {
            return isClubRegistrationFormValid(this.club, this.assignedMembershipTypes, this.validation, {
                requireTerms: true,
                requireRenewalOptIn: this.isRenewalOptInVisible
            });
        },

        isReaffiliationMode() {
            return !!(this.$route && this.$route.path && this.$route.path.startsWith('/dashboard/club/reaffiliate/'));
        },

        isNewClubMode() {
            return !(this.$route && this.$route.params && this.$route.params.id);
        },

        isRenewalOptInVisible() {
            if (this.isReaffiliationMode) {
                return true;
            }

            if (this.isNewClubMode) {
                return false;
            }

            return this.club.isRenewalOptInOffered === true;
        }
    },

    methods: {
        showError(message) {
            this.$showError(message || 'Something went wrong. Incorrect request!', 10000);
        },

        showSuccess(message) {
            this.$showSuccess(message || 'Operation success!', 10000);
        },

        setMembershipFieldError(field, message) {
            const validation = this.membershipFieldValidation[field];
            if (!validation) return;

            validation.valid = false;
            validation.errorMessages = [message];
        },

        clearMembershipFieldError(field) {
            const validation = this.membershipFieldValidation[field];
            if (!validation) return;

            validation.valid = true;
            validation.errorMessages = [];
        },

        clearAssignMembershipTypeValidation() {
            this.clearMembershipFieldError('selectedMembershipType');
            this.clearMembershipFieldError('membershipTypePrice');
        },

        clearNewMembershipTypeValidation() {
            this.clearMembershipFieldError('newMembershipTypeName');
        },

        isTemporaryMembershipTypeId(id) {
            return Number(id) < 0;
        },
        
        hasValidationErrors(sectionId) {
            return hasClubRegistrationValidationErrors(sectionId, this.club, this.assignedMembershipTypes, this.validation, {
                requireTerms: true,
                requireRenewalOptIn: this.isRenewalOptInVisible
            });
        },

        async loadData() {
            try {
                this.isLoading = true;
                this.initData.yearOptions = generateYearOptions();
                this.initData.countyOptions = formatCountyOptions(IRISH_COUNTIES);
                await this.loadMembershipTypes();
            } catch (error) {
                this.errorMessage = error.message || 'Failed to load data';
            } finally {
                this.isLoading = false;
            }
        },

        async loadRenewalOptInLockState() {
            try {
                const response = await resourceService.getClubRenewalOptInLockState();
                if (response && response.body && response.body.isSuccess) {
                    this.isRenewalOptInLocked = !!(response.body.outputData && response.body.outputData.isLocked);
                    if (this.isRenewalOptInLocked) {
                        this.club.allowClubRenewalOptIn = 'No';
                    }
                }
            } catch (error) {
                console.error('Failed to load club renewal opt-in lock state:', error);
            }
        },

        async loadPreviousYearClubs() {
            try {
                const response = await resourceService.getPreviousYearClubs();
                if (response && response.body && response.body.isSuccess) {
                    const clubs = response.body.outputData || [];
                    this.copyOptions = clubs.map(c => ({
                        label: `${c.clubName} (${c.year})`,
                        value: c.clubId,
                        year: c.year
                    }));
                }
            } catch (error) {
                console.error('Failed to load previous clubs:', error);
            }
        },

        async loadMembershipTypes() {
            try {
                const response = await resourceService.getAllMembershipTypes();
                if (response && response.body && response.body.isSuccess) {
                    const types = response.body.outputData || [];
                    this.initData.availableMembershipTypes = types.map(t => ({
                        label: t.clubMembershipTypeLabel || t.clubMembershipType,
                        value: t.clubMembershipTypeId
                    }));
                }
            } catch (error) {
                console.error('Failed to load membership types:', error);
            }
        },

        async copyFromClub() {
            if (!this.selectedCopyId) {
                this.showError('Please select a club to copy from');
                return;
            }

            try {
                this.isLoadingCopy = true;
                const selectedOption = this.copyOptions.find(o => o.value === this.selectedCopyId);
                const response = await resourceService.getClubDetailsForCopy(this.selectedCopyId, selectedOption && selectedOption.year);
                
                if (response && response.body && response.body.isSuccess) {
                    this.applySourceClubData(response.body.outputData);
                    this.showSuccess('Club details loaded successfully. Please review and update as needed.');
                } else {
                    const errorMsg = (response && response.body && response.body.message) || 'Failed to load club details';
                    this.showError(errorMsg);
                }
            } catch (error) {
                this.showError(error.message || 'An error occurred while loading club details');
            } finally {
                this.isLoadingCopy = false;
            }
        },

        applySourceClubData(sourceClub) {
            Object.assign(this.club, {
                name: sourceClub.name,
                clubEmail: sourceClub.clubEmail,
                clubWebsite: sourceClub.clubWebsite,
                town: sourceClub.town,
                county: sourceClub.county,
                yearFormed: parseIntOrNull(sourceClub.yearFormed),
                noOfMembersLastYear: parseIntOrNull(sourceClub.noOfMembersLastYear),
                membersLimit: sourceClub.membersLimit,
                facebook: sourceClub.facebook,
                twitter: sourceClub.twitter,
                allowClubRenewalOptIn: !this.isRenewalOptInVisible
                    ? null
                    : (this.isRenewalOptInLocked
                        ? 'No'
                        : (sourceClub.allowClubRenewalOptIn || null))
            });

            this.club.chairperson = normalizeOfficer(sourceClub.chairperson);
            this.club.treasurer = normalizeOfficer(sourceClub.treasurer);
            this.club.secretary = normalizeOfficer(sourceClub.secretary);
            this.club.membershipOfficer = normalizeOfficer(sourceClub.membershipOfficer);
            this.club.womensOfficer = normalizeOfficer(sourceClub.womensOfficer);
            this.club.safeguardingOfficer = normalizeOfficer(sourceClub.safeguardingOfficer);
            this.club.inclusionDiversityOfficer = normalizeOfficer(sourceClub.inclusionDiversityOfficer);
            this.club.designatedLiaisonPerson = normalizeOfficer(sourceClub.designatedLiaisonPerson);
            this.club.additionalOfficers = (sourceClub.additionalOfficers || []).map(normalizeAdditionalOfficer);

            this.club.hasJuniorMembers = sourceClub.hasJuniorMembers === 'Yes';
            this.club.juniorMembersLimit = parseIntOrNull(sourceClub.juniorMembersLimit);
            this.club.juniorAgeGroups = [...(sourceClub.juniorAgeGroups || [])];
            this.club.juniorAgeGroupsAdditional = sourceClub.juniorAgeGroupsAdditional;

            Object.assign(this.club, {
                bankAccountName: sourceClub.bankAccountName,
                bankName: sourceClub.bankName,
                bankAddress: sourceClub.bankAddress,
                iban: sourceClub.iban,
                bicNumber: sourceClub.bicNumber
            });

            this.club.safeguardStatementLink = sourceClub.safeguardStatementLink;
            this.club.safeguardStatementDescription = sourceClub.safeguardStatementDescription;
            this.club.notificationState = sourceClub.notificationState;
            this.club.notificationEmailAddress = sourceClub.notificationEmailAddress;

            this.assignedMembershipTypes = sourceClub.clubMembershipTypes.map((t, idx) => ({
                id: -(idx + 1),
                clubMembershipTypeId: t.clubMembershipTypeId,
                name: t.clubMembershipTypeLabel,
                price: t.price
            }));

            this.club.id = null;
            this.club.acceptTerms = false;
            this.club.termsConditionsFiles = [];
            this.club.safeguardingStatementFiles = [];
            this.club.year = new Date().getFullYear();
        },

        assignMembershipType() {
            this.clearAssignMembershipTypeValidation();

            if (!this.selectedMembershipTypeId) {
                this.setMembershipFieldError('selectedMembershipType', 'Please select a membership type');
            }

            if (
                this.selectedMembershipTypePrice === null ||
                this.selectedMembershipTypePrice === undefined ||
                this.selectedMembershipTypePrice === '' ||
                this.selectedMembershipTypePrice < 0
            ) {
                this.setMembershipFieldError('membershipTypePrice', 'Please enter a valid price');
            }

            if (
                !this.membershipFieldValidation.selectedMembershipType.valid ||
                !this.membershipFieldValidation.membershipTypePrice.valid
            ) {
                this.showError('Please complete the membership type fields');
                return;
            }

            const selectedType = this.initData.availableMembershipTypes.find(
                t => t.value === this.selectedMembershipTypeId
            );

            if (!selectedType) return;

            const tempId = -(this.assignedMembershipTypes.length + 1);
            this.assignedMembershipTypes.push({
                id: tempId,
                clubMembershipTypeId: selectedType.value,
                name: selectedType.label,
                price: this.selectedMembershipTypePrice,
                isTemporary: selectedType.isTemporary === true
            });

            this.resetMembershipTypeForm();
        },

        removeMembershipType(index) {
            const removedTypes = this.assignedMembershipTypes.splice(index, 1);
            const removedType = removedTypes && removedTypes.length ? removedTypes[0] : null;

            if (!removedType) return;

            if (removedType.isTemporary || this.isTemporaryMembershipTypeId(removedType.clubMembershipTypeId)) {
                this.initData.availableMembershipTypes = this.initData.availableMembershipTypes.filter(
                    t => t.value !== removedType.clubMembershipTypeId
                );
            }
        },

        addNewMembershipType() {
            const trimmedName = this.newMembershipTypeName ? this.newMembershipTypeName.trim() : '';
            this.clearNewMembershipTypeValidation();
            
            if (!trimmedName) {
                this.setMembershipFieldError('newMembershipTypeName', 'Please enter a membership type name');
                this.showError('Please enter a membership type name');
                return;
            }

            const nameExists = this.initData.availableMembershipTypes.some(
                t => t.label.toLowerCase() === trimmedName.toLowerCase()
            );

            if (nameExists) {
                this.setMembershipFieldError('newMembershipTypeName', 'A membership type with this name already exists');
                this.showError('A membership type with this name already exists');
                return;
            }

            const tempId = this.nextTemporaryMembershipTypeId--;
            this.initData.availableMembershipTypes.push({
                label: trimmedName,
                value: tempId,
                isTemporary: true
            });

            this.selectedMembershipTypeId = tempId;
            this.newMembershipTypeName = '';
            this.clearMembershipFieldError('selectedMembershipType');
            this.clearNewMembershipTypeValidation();
            this.showSuccess('New membership type created. Set the price and click Assign.');
        },

        resetMembershipTypeForm() {
            this.selectedMembershipTypeId = null;
            this.selectedMembershipTypePrice = null;
            this.clearAssignMembershipTypeValidation();
        },

        getPayload() {
            const { club } = this;
            
            return {
                id: club.id,
                name: club.name,
                membersLimit: club.membersLimit,
                year: club.year,
                clubWebsite: club.clubWebsite,
                clubEmail: club.clubEmail,
                town: club.town,
                county: club.county,
                noOfMembersLastYear: toStringOrEmpty(club.noOfMembersLastYear),
                yearFormed: toStringOrEmpty(club.yearFormed),
                facebook: club.facebook || '',
                twitter: club.twitter || '',
                hasJuniorMembers: club.hasJuniorMembers === true ? 'Yes' : 'No',
                allowClubRenewalOptIn: this.isRenewalOptInVisible
                    ? (club.allowClubRenewalOptIn || null)
                    : null,
                juniorMembersLimit: toStringOrEmpty(club.juniorMembersLimit),
                juniorAgeGroups: club.juniorAgeGroups || [],
                juniorAgeGroupsAdditional: club.juniorAgeGroupsAdditional || '',
                chairperson: club.chairperson || createEmptyOfficer(),
                treasurer: club.treasurer || createEmptyOfficer(),
                secretary: club.secretary || createEmptyOfficer(),
                membershipOfficer: club.membershipOfficer || createEmptyOfficer(),
                womensOfficer: club.womensOfficer || createEmptyOfficer(),
                inclusionDiversityOfficer: club.inclusionDiversityOfficer || createEmptyOfficer(),
                safeguardingOfficer: club.safeguardingOfficer || createEmptyOfficer(),
                designatedLiaisonPerson: club.designatedLiaisonPerson || createEmptyOfficer(),
                additionalOfficers: club.additionalOfficers || [],
                bankAccountName: club.bankAccountName || '',
                bankName: club.bankName || '',
                bankAddress: club.bankAddress || '',
                iban: club.iban || '',
                bicNumber: club.bicNumber || '',
                safeguardStatementLink: club.safeguardStatementLink || '',
                safeguardStatementDescription: club.safeguardStatementDescription || '',
                feePaymentType: club.feePaymentType || 'Online',
                acceptTerms: club.acceptTerms === true,
                notificationState: club.notificationState === true,
                notificationEmailAddress: club.notificationEmailAddress || '',
                clubMembershipTypes: this.assignedMembershipTypes.map(t => ({
                    id: t.id || 0,
                    clubMembershipTypeId: t.clubMembershipTypeId,
                    clubMembershipTypeLabel: t.name || '',
                    clubMembershipType: t.name || '',
                    price: Number(t.price) || 0
                })),
                termsConditionsFiles: club.termsConditionsFiles || [],
                safeguardingStatementFiles: club.safeguardingStatementFiles || [],
                userMembersCount: 0,
                acknowledgedMembershipTypeRemovals: this.acknowledgedMembershipTypeRemovals === true
            };
        },

        async saveClub() {
            if (!this.validateBeforeSave()) return;

            try {
                this.isSaving = true;
                const response = await resourceService.saveClubRegistration(this.getPayload());
                
                if (response && response.body && response.body.isSuccess) {
                    await this.handleSaveSuccess(response.body.outputData);
                } else {
                    const errorMsg = (response && response.body && response.body.message) || 'Failed to save club';
                    this.showError(errorMsg);
                    this.isSaving = false;
                }
            } catch (error) {
                this.showError(error.message || 'An error occurred');
                this.isSaving = false;
            }
        },

        validateBeforeSave() {
            if (!this.isFormValid) {
                this.showError('Please fill in all required fields and correct invalid values');
                return false;
            }

            return true;
        },

        async handleSaveSuccess(result) {
            if (result && result.status === 'PaymentRequired' && result.clubId) {
                await this.handlePaymentRequired(result.clubId);
            } else if (result && result.status === 'Saved' && result.clubId) {
                this.handleSavedWithoutPayment();
            } else {
                this.handleUnknownStatus();
            }
        },

        async handlePaymentRequired(clubId) {
            this.showSuccess('Club registration saved. Redirecting to payment...');

            try {
                const paymentResponse = await resourceService.prepareClubAffiliationPayment(clubId);
                
                if (paymentResponse && paymentResponse.body && paymentResponse.body.isSuccess) {
                    const redirectUrl = paymentResponse.body.redirectUrl || `/club/billing/${clubId}`;
                    this.isSaving = false;
                    window.location.href = redirectUrl;
                } else {
                    const errorMsg = (paymentResponse && paymentResponse.body && paymentResponse.body.message) || 'Failed to prepare payment';
                    this.showError(errorMsg);
                    this.isSaving = false;
                }
            } catch (paymentError) {
                this.showError(paymentError.message || 'Failed to prepare payment');
                this.isSaving = false;
            }
        },

        handleSavedWithoutPayment() {
            this.showSuccess('Club registration completed successfully');
            this.isSaving = false;
            this.$router.push('/dashboard/club/management');
        },

        handleUnknownStatus() {
            this.showSuccess('Club registration completed. You can view it in your managed clubs.');
            this.isSaving = false;
            this.$router.push('/dashboard/club/management');
        },

        cancelChanges() {
            if (confirm('Are you sure you want to discard your changes?')) {
                this.$router.push({ path: '/dashboard/club/management' });
            }
        },

        goToNextSection() {
            if (this.nextSectionId) {
                this.navigateToSection(this.nextSectionId);
            }
        },

        goToPreviousSection() {
            if (this.previousSectionId) {
                this.navigateToSection(this.previousSectionId);
            }
        },

        navigateToSection(sectionId) {
            this.activeSection = sectionId;
            this.$nextTick(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        },

        handlePageShow(event) {
            if (event.persisted) {
                this.isSaving = false;
            }
        },

        async loadReaffiliationPrefill() {
            const clubId = parseInt(this.$route.params.id, 10);
            if (isNaN(clubId)) {
                this.errorMessage = 'Invalid club ID.';
                return;
            }
            const reaffYear = parseInt(this.$route.query.year, 10) || null;
            const sourceYear = parseInt(this.$route.query.srcYear, 10) || null;
            this.isLoading = true;
            try {
                const response = await resourceService.getClubDetailsForCopy(clubId, sourceYear);
                if (response && response.body && response.body.isSuccess) {
                    this.applySourceClubData(response.body.outputData);
                    this.club.id = clubId;
                    if (reaffYear) {
                        this.club.year = reaffYear;
                    }
                } else {
                    this.errorMessage = (response && response.body && response.body.message) || 'Failed to load club details for reaffiliation.';
                }
            } catch (error) {
                this.errorMessage = error.message || 'An error occurred while loading club details for reaffiliation.';
            } finally {
                this.isLoading = false;
            }
        },

        confirmMembershipTypeRemovals(removedMembershipTypes) {
            this.removalWarning = buildMembershipTypeRemovalWarning(removedMembershipTypes);
            this.isRemovalWarningVisible = true;
        },

        cancelMembershipTypeRemovals() {
            this.isRemovalWarningVisible = false;
            this.removalWarning = null;
        },

        async acceptMembershipTypeRemovals() {
            this.isRemovalWarningVisible = false;
            this.removalWarning = null;
            this.acknowledgedMembershipTypeRemovals = true;
            await this.reaffiliateClub();
        },

        async reaffiliateClub() {
            if (!this.validateBeforeSave()) return;

            try {
                this.isSaving = true;
                const response = await resourceService.reaffiliateClub(this.getPayload());

                if (response && response.body && response.body.isSuccess) {
                    const result = response.body.outputData;

                    if (result && result.status === 'ConfirmationRequired') {
                        this.isSaving = false;
                        this.confirmMembershipTypeRemovals(result.removedMembershipTypes);
                        return;
                    }

                    await this.handleSaveSuccess(result);
                } else {
                    const errorMsg = (response && response.body && response.body.message) || 'Failed to reaffiliate club';
                    this.showError(errorMsg);
                    this.isSaving = false;
                }
            } catch (error) {
                this.showError(error.message || 'An error occurred');
                this.isSaving = false;
            }
        },
    },

    watch: {
        assignedMembershipTypes() {
            this.acknowledgedMembershipTypeRemovals = false;
        },

        selectedMembershipTypeId(value) {
            if (value) {
                this.clearMembershipFieldError('selectedMembershipType');
            }
        },

        selectedMembershipTypePrice(value) {
            if (value !== null && value !== undefined && value !== '' && value >= 0) {
                this.clearMembershipFieldError('membershipTypePrice');
            }
        },

        newMembershipTypeName(value) {
            if (value && value.trim()) {
                this.clearMembershipFieldError('newMembershipTypeName');
            }
        }
    },

    async mounted() {
        window.addEventListener('pageshow', this.handlePageShow);
        await this.loadRenewalOptInLockState();
        this.loadPreviousYearClubs();
        if (this.isReaffiliationMode) {
            await this.loadData();
            await this.loadReaffiliationPrefill();
        } else {
            this.loadData();
        }
    },

    beforeDestroy() {
        window.removeEventListener('pageshow', this.handlePageShow);
    }
};



// WEBPACK FOOTER //
// ./src/components/user/dashboard/pages/clubs/register-club/register-club.js